/**
 * ZSL Derivation Utility
 *
 * This module handles the synchronization between canvas state and ZSL state.
 * It provides a mechanism to derive an updated ZSL based on changes in the canvas,
 * while preserving existing ZSL data and maintaining consistency.
 *
 * Supported Operations:
 * 1. Adding new assets to the ZSL from canvas nodes
 * 2. Removing assets from the ZSL when they no longer exist in canvas nodes
 * 3. Updating the name or description of the ZSL based on canvas metadata
 *
 * How it works:
 * 1. Takes the current canvas state (nodes and metadata) and current ZSL as input
 * 2. Preserves all existing ZSL data that isn't directly modified
 * 3. Tracks which assets exist in the current canvas nodes
 * 4. Removes assets that no longer exist in the canvas
 * 5. Adds or updates assets based on the current canvas nodes
 * 6. Only returns a new ZSL object if actual changes are detected
 *
 * Asset Types Supported:
 * - Tables
 * - Interfaces
 * - Zaps
 * - Chatbots
 * - Agents
 *
 * The module ensures that:
 * - Existing ZSL data is preserved when not directly modified
 * - Asset relationships are maintained
 * - Changes are only applied when necessary
 * - The ZSL state remains consistent with the canvas state
 */

import { Node } from "@xyflow/react";
import { atom } from "jotai";
import { ZSL } from "../../types";
import { formsTerminologyAtom } from "../../diagramming/togglesAtom";

// We only support:
// - adding new assets to the ZSL,
// - removing assets from the ZSL
// - updating the name or description of the ZSL

/**
 * Atom that derives an updated ZSL object based on canvas and nodes data.
 *
 * @returns A function that returns a new ZSL object or null if no changes
 *
 * **Function Parameters:**
 * - currentZsl - The current ZSL state
 * - canvas - The canvas metadata
 * - nodes - The canvas nodes
 *
 * **Function Returns:**
 * A new ZSL object if changes were detected, or null if no changes
 */
export const deriveZslFromCanvasAtom = atom((get) => {
  const getZslAssetFromCanvasNode = get(getZslAssetFromCanvasNodeAtom);

  return (
    currentZsl: ZSL,
    canvas: { id: string; name: string; description: string | null },
    nodes: Node[],
  ): ZSL | null => {
    // Start with a copy of the current ZSL, preserving all existing data
    const newZsl: ZSL = {
      ...currentZsl,
      id: canvas.id, // Always use the new canvas id
      key: currentZsl?.key || "",
      name: canvas.name || currentZsl?.name || "",
      description: canvas.description || currentZsl?.description || undefined,
      slug: currentZsl?.slug || "",
      canvases: currentZsl?.canvases || [],
      tables: currentZsl?.tables || [],
      interfaces: currentZsl?.interfaces || [], // Preserve existing interfaces
      zaps: currentZsl?.zaps || [],
      parameters: currentZsl?.parameters || [],
      chatbots: currentZsl?.chatbots || [],
      agents: currentZsl?.agents || [],
    };

    // Track which assets exist in the current nodes
    const nodeAssets = new Set<string>();
    const nodeAssetTypes = new Map<
      string,
      "tables" | "interfaces" | "zaps" | "chatbots" | "agents"
    >();

    // Process nodes to identify current assets
    nodes.forEach((node) => {
      const assetInfo = getZslAssetFromCanvasNode(node);
      if (!assetInfo) return;

      const { type, zslAsset } = assetInfo;
      nodeAssets.add(zslAsset.id);
      nodeAssetTypes.set(zslAsset.id, type);
    });

    // Remove assets that no longer exist in nodes
    ["tables", "interfaces", "zaps", "chatbots", "agents"].forEach(
      (typeStr) => {
        switch (typeStr) {
          case "tables":
            if (newZsl.tables) {
              newZsl.tables = newZsl.tables.filter((asset) => {
                const existsInNodes = nodeAssets.has(asset.id);
                if (!existsInNodes) return false;
                return nodeAssetTypes.get(asset.id) === "tables";
              });
            }
            break;
          case "interfaces":
            if (newZsl.interfaces) {
              newZsl.interfaces = newZsl.interfaces.filter((asset) => {
                const existsInNodes = nodeAssets.has(asset.id);
                if (!existsInNodes) return false;
                return nodeAssetTypes.get(asset.id) === "interfaces";
              });
            }
            break;
          case "zaps":
            if (newZsl.zaps) {
              newZsl.zaps = newZsl.zaps.filter((asset) => {
                const existsInNodes = nodeAssets.has(asset.id);
                if (!existsInNodes) return false;
                return nodeAssetTypes.get(asset.id) === "zaps";
              });
            }
            break;
          case "chatbots":
            if (newZsl.chatbots) {
              newZsl.chatbots = newZsl.chatbots.filter((asset) => {
                const existsInNodes = nodeAssets.has(asset.id);
                if (!existsInNodes) return false;
                return nodeAssetTypes.get(asset.id) === "chatbots";
              });
            }
            break;
          case "agents":
            if (newZsl.agents) {
              newZsl.agents = newZsl.agents.filter((asset) => {
                const existsInNodes = nodeAssets.has(asset.id);
                if (!existsInNodes) return false;
                return nodeAssetTypes.get(asset.id) === "agents";
              });
            }
            break;
        }
      },
    );

    // Add new zsl assets from nodes and update existing ones
    nodes.forEach((node) => {
      const zslAssetInfo = getZslAssetFromCanvasNode(node);
      if (!zslAssetInfo) return;

      const { type, zslAsset } = zslAssetInfo;

      // Check if asset exists
      const existingAssetIndex =
        newZsl[type]?.findIndex((a) => a.id === zslAsset.id) ?? -1;

      if (existingAssetIndex >= 0) {
        // Update existing asset while preserving all properties
        const existingAsset = newZsl[type]![existingAssetIndex];
        newZsl[type]![existingAssetIndex] = {
          ...existingAsset,
          ...zslAsset, // Merge in new properties while preserving existing ones
        };
      } else {
        // Initialize array if it doesn't exist
        if (!newZsl[type]) {
          newZsl[type] = [];
        }

        // Add the new asset
        newZsl[type]!.push(zslAsset);
      }
    });

    // Only return the new ZSL if there are actual changes
    const hasChanges =
      currentZsl?.id !== newZsl.id ||
      currentZsl?.name !== newZsl.name ||
      currentZsl?.description !== newZsl.description ||
      JSON.stringify(currentZsl?.tables) !== JSON.stringify(newZsl.tables) ||
      JSON.stringify(currentZsl?.interfaces) !==
        JSON.stringify(newZsl.interfaces) ||
      JSON.stringify(currentZsl?.zaps) !== JSON.stringify(newZsl.zaps) ||
      JSON.stringify(currentZsl?.chatbots) !==
        JSON.stringify(newZsl.chatbots) ||
      JSON.stringify(currentZsl?.agents) !== JSON.stringify(newZsl.agents);

    if (!hasChanges) return null;

    return newZsl;
  };
});
deriveZslFromCanvasAtom.debugLabel = "deriveZslFromCanvasAtom";

/**
 * Atom that extracts zsl asset information from a canvas node.
 *
 * This atom identifies and extracts the relevant asset data from a canvas node
 * based on its type and data properties. It supports five types of assets:
 * - Tables (identified by tableId)
 * - Interfaces (identified by pageId)
 * - Zaps (identified by node type)
 * - Chatbots (identified by node type or chatbotId)
 * - Agents (identified by agentId)
 *
 * @returns A function that accepts a node and returns asset info or null
 */
export const getZslAssetFromCanvasNodeAtom = atom((get) => {
  const interfacesTerms = get(formsTerminologyAtom);

  return (
    node: Node,
  ): {
    type: "tables" | "interfaces" | "zaps" | "chatbots" | "agents";
    zslAsset: any;
  } | null => {
    const { data } = node;

    if (data.tableId) {
      return {
        type: "tables",
        zslAsset: {
          id: data.tableId,
          name: data.label || "Untitled Table",
        },
      };
    }

    if (data.pageId) {
      return {
        type: "interfaces",
        zslAsset: {
          id: data.projectId,
          name: data.label || `Untitled ${interfacesTerms.singular}`,
        },
      };
    }

    if (node.type === "zap") {
      return {
        type: "zaps",
        zslAsset: {
          id: data.zapId || node.id,
          title: data.label || "Untitled Zap",
        },
      };
    }

    if (node.type === "chatbot" || data.chatbotId) {
      return {
        type: "chatbots",
        zslAsset: {
          id: data.chatbotId || node.id,
          name: data.label || "Untitled Chatbot",
        },
      };
    }

    if (data.agentId) {
      return {
        type: "agents",
        zslAsset: {
          id: data.agentId,
          name: data.label || "Untitled Agent",
        },
      };
    }

    return null;
  };
});
getZslAssetFromCanvasNodeAtom.debugLabel = "getZslAssetFromCanvasNodeAtom";
